import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, catchError, shareReplay, tap, throwError } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import type { MeProfile } from '../models/user.model';

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  phone?: string;
  bio?: string;
}

export interface StripeStatus {
  stripeDetailsSubmitted: boolean;
  stripeChargesEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class UsersService {
  private readonly meState = signal<MeProfile | null>(null);
  private meRequest: Observable<MeProfile> | null = null;

  /** Cached `/users/me` snapshot, richer than `AuthService.currentUser`. Shared
   * so several account panels can read it off one request. */
  readonly me = this.meState.asReadonly();

  constructor(private readonly http: HttpClient) {}

  loadMe(): Observable<MeProfile> {
    if (!this.meRequest) {
      this.meRequest = this.http.get<MeProfile>(`${API_BASE_URL}/users/me`).pipe(
        tap((me) => this.meState.set(me)),
        // Don't let a failed request stay cached — the next caller should retry.
        catchError((error: unknown) => {
          this.meRequest = null;
          return throwError(() => error);
        }),
        shareReplay(1),
      );
    }
    return this.meRequest;
  }

  /** Drops the cache so the next `loadMe()` refetches — call after editing the
   * profile or returning from Stripe onboarding. Leaves the last-known value
   * in `me()` until the refetch resolves, which is fine when it's still the
   * same person's data mid-refresh. */
  invalidateMe(): void {
    this.meRequest = null;
  }

  /** Like `invalidateMe()`, but also blanks `me()` immediately rather than
   * leaving the previous snapshot visible. Called by AuthService whenever
   * the logged-in identity itself changes (login/signup as someone new,
   * logout) — `me()` carries KYC/verification status and other
   * identity-sensitive fields, so it must never keep showing one account's
   * data after a different account becomes "current" in the same tab. */
  resetMe(): void {
    this.meRequest = null;
    this.meState.set(null);
  }

  updateMe(payload: UpdateProfilePayload): Observable<unknown> {
    return this.http
      .patch(`${API_BASE_URL}/users/me`, payload)
      .pipe(tap(() => this.invalidateMe()));
  }

  /** Creates (or resumes) a Stripe Connect Express account and returns a
   * fresh hosted-onboarding URL to redirect the seller to. */
  createStripeOnboardingLink(): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${API_BASE_URL}/users/me/stripe-onboarding-link`, {});
  }

  /** Forces a re-sync of KYC flags from Stripe — call when the seller
   * returns from the hosted onboarding flow. */
  refreshStripeStatus(): Observable<StripeStatus> {
    return this.http.post<StripeStatus>(`${API_BASE_URL}/users/me/stripe-status/refresh`, {});
  }
}
